/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * WebSocket connection states (REQ-TRANS-07).
 */
export enum WebSocketConnectionState {
  DISCONNECTED = "DISCONNECTED",
  CONNECTING = "CONNECTING",
  CONNECTED = "CONNECTED",
  CLOSING = "CLOSING"
}

/**
 * Named connection triggers (REQ-LIFE-02, REQ-WEB-LIFE-02).
 */
export enum ConnectionTrigger {
  PAGE_LOAD = "PAGE_LOAD",
  PAGE_RELOAD = "PAGE_RELOAD",
  APP_FOREGROUND = "APP_FOREGROUND",
  NETWORK_RESTORED = "NETWORK_RESTORED",
  NEW_BODY_ID_RECEIVED = "NEW_BODY_ID_RECEIVED",
  NEW_ACCESS_TOKEN_RECEIVED = "NEW_ACCESS_TOKEN_RECEIVED",
  SERVICE_CALL = "SERVICE_CALL",
  UX_REFRESH_ACCOUNT = "UX_REFRESH_ACCOUNT",
  SOFT_CLIENT_CERTIFICATE_EXPIRED = "SOFT_CLIENT_CERTIFICATE_EXPIRED",
  USER_ACTIVITY = "USER_ACTIVITY",
  DEBUG_SWITCH_ENVIRONMENT = "DEBUG_SWITCH_ENVIRONMENT"
}

/**
 * Observable connection states published to consumers (REQ-LIFE-06).
 */
export enum WebSocketObservableState {
  Idle = "Idle",
  Connecting = "Connecting",
  Connected = "Connected",
  Error = "Error",
  Reconnecting = "Reconnecting"
}

/**
 * Internal FSM states for the connection establishment sequence (REQ-FSM-01).
 *
 * The 8-state FSM drives the connect/restart/stop lifecycle.
 * Observable state (WebSocketObservableState) is what consumers see;
 * FSM state is the internal phase tracked by the Orchestrator.
 */
export enum WebSocketFsmState {
  /** Terminal/dwell — subsystem at rest (REQ-FSM-05). */
  IDLE = "IDLE",
  /** Transient — initialisation bookkeeping before cert acquisition (REQ-FSM-06). */
  START = "START",
  /** Restartable — acquire soft client certificate (REQ-FSM-07). */
  GET_CLIENT_CERT = "GET_CLIENT_CERT",
  /** Restartable — resolve WSS endpoint via TSRI (REQ-FSM-07). */
  GET_SECURE_ENDPOINT = "GET_SECURE_ENDPOINT",
  /** Restartable — evaluate local connection permission (REQ-FSM-07). */
  CHECK_BRIDGEKEEPER = "CHECK_BRIDGEKEEPER",
  /** Restartable — open the WSS connection (REQ-FSM-07). */
  ESTABLISH_CONTEXT = "ESTABLISH_CONTEXT",
  /** Transient — ordered teardown (REQ-FSM-06). */
  STOP = "STOP",
  /** Transient — re-entry to GET_CLIENT_CERT after an error (REQ-FSM-06). */
  RESTART = "RESTART"
}

/**
 * MRPCv2 message type (REQ-SER-04).
 */
export enum MrpcMessageType {
  REQUEST = "request",
  RESPONSE = "response"
}

/**
 * Internal WebSocket message model (REQ-SER-04).
 */
export interface IWebSocketMessage {
  /** Message direction. */
  type: MrpcMessageType;
  /** RPC operation identifier. */
  operationName: string | null;
  /** Unique message identifier (RpcId). */
  id: string;
  /** ID of the originating request for correlation. */
  originRequestId: string;
  /** Protocol-level metadata. */
  headers: Record<string, string>;
  /** Serialized JSON payload. */
  body: string;
  /** Whether a response is expected from the client (ResponseCount === "single"). */
  isResponseExpected: boolean;
}

/**
 * Transport-level events emitted to the orchestrator (REQ-TRANS-03).
 */
export enum TransportEventType {
  Open = "Open",
  TextMessage = "TextMessage",
  BinaryMessage = "BinaryMessage",
  Error = "Error",
  Closed = "Closed"
}

export interface ITransportOpenEvent {
  /** Discriminator for the Open event type. */
  type: TransportEventType.Open;
  /** Negotiated sub-protocol name. */
  protocol: string;
  /** Server-requested ping interval in seconds (from x-tivo-ping-interval header, if available). */
  pingIntervalSeconds: number;
}

export interface ITransportTextMessageEvent {
  /** Discriminator for the TextMessage event type. */
  type: TransportEventType.TextMessage;
  /** The raw text frame received from the server. */
  data: string;
}

export interface ITransportBinaryMessageEvent {
  /** Discriminator for the BinaryMessage event type. */
  type: TransportEventType.BinaryMessage;
  /** The raw binary data received from the server. */
  data: ArrayBuffer;
  /** The byte length of the binary data. */
  length: number;
}

export interface ITransportErrorEvent {
  /** Discriminator for the Error event type. */
  type: TransportEventType.Error;
  /** Typed error code from WebSocketErrorCode. */
  code: string;
  /** Human-readable error description. */
  detail: string;
}

export interface ITransportClosedEvent {
  /** Discriminator for the Closed event type. */
  type: TransportEventType.Closed;
  /** Close code (RFC 6455 or application-defined 3xxx). */
  code: number;
  /** Human-readable close reason. */
  detail: string;
}

export type TransportEvent =
  | ITransportOpenEvent
  | ITransportTextMessageEvent
  | ITransportBinaryMessageEvent
  | ITransportErrorEvent
  | ITransportClosedEvent;

/**
 * Callback type for transport event listeners.
 */
export type TransportEventListener = (event: TransportEvent) => void;

/**
 * Callback type for observable state listeners (REQ-LIFE-06).
 */
export type WebSocketStateListener = (
  state: WebSocketObservableState,
  detail?: string
) => void;

/**
 * Callback type for incoming message handlers (REQ-ROUTE-01).
 */
export type OperationHandler = (
  message: IWebSocketMessage
) => Promise<Record<string, unknown> | null>;

/**
 * Configuration for the WebSocket subsystem.
 */
export interface IWebSocketConfig {
  /** WSS endpoint host (from TSRI). */
  host: string;
  /** WSS endpoint port (from TSRI). */
  port: number;
  /** WSS path (default: ""). */
  path?: string;
  /** Sub-protocol to negotiate (default: "com.tivo.mindrpc.2"). */
  subProtocol?: string;
  /** Default ping interval in seconds if server doesn't specify (REQ-TRANS-06). */
  defaultPingIntervalSeconds?: number;
  /** Maximum inbound message size in bytes (REQ-TRANS-08). */
  maxInboundMessageSize?: number;
  /** Outbound ring buffer capacity (REQ-TRANS-09). */
  outboundBufferCapacity?: number;
  /** JWT validity window in seconds (REQ-WEB-TRANS-02 §4.6.3). */
  jwtValiditySeconds?: number;

  /**
   * FSM GET_CLIENT_CERT state processor delegate (REQ-FSM-07).
   *
   * Called on every connection attempt (including RESTART) to ensure
   * a valid certificate exists in DeviceModel/IndexedDB.
   * If the certificate is expired or missing, the delegate MUST
   * acquire a fresh one before resolving.
   *
   * If not provided, runConnectFlow() reads whatever is in storage.
   */
  ensureCertificate?: () => Promise<void>;

  /**
   * FSM GET_SECURE_ENDPOINT state processor delegate (REQ-FSM-07).
   *
   * Called on every connection attempt (including RESTART) to resolve
   * a fresh WSS host and port via TSRI.
   *
   * If not provided, the static host/port from config are used on
   * every attempt (no re-resolution on RESTART).
   */
  resolveEndpoint?: () => Promise<{ host: string; port: number }>;
}

/**
 * Ring buffer entry for outbound messages (REQ-TRANS-09).
 */
export interface IOutboundBufferEntry {
  /** The serialized MRPCv2 frame string to send. */
  frame: string;
  /** Unix timestamp (ms) when the entry was enqueued. */
  timestamp: number;
}

/**
 * Error codes for WebSocket subsystem errors.
 */
export enum WebSocketErrorCode {
  BUFFER_FULL = "BUFFER_FULL",
  MESSAGE_TOO_LARGE = "MESSAGE_TOO_LARGE",
  NOT_CONNECTED = "NOT_CONNECTED",
  ALREADY_CLOSING = "ALREADY_CLOSING",
  JWT_SIGNING_ERROR = "JWT_SIGNING_ERROR",
  CERTIFICATE_MISSING = "CERTIFICATE_MISSING",
  PRIVATE_KEY_MISSING = "PRIVATE_KEY_MISSING",
  CERTIFICATE_PARSE_ERROR = "CERTIFICATE_PARSE_ERROR",
  UNSUPPORTED_PROTOCOL = "UNSUPPORTED_PROTOCOL",
  ENVELOPE_ERROR = "ENVELOPE_ERROR",
  DESERIALIZE_ERROR = "DESERIALIZE_ERROR",
  FEATURE_NOT_ENABLED = "FEATURE_NOT_ENABLED",
  ROUTING_ERROR = "ROUTING_ERROR",
  SCHEMA_EXCEPTION = "SCHEMA_EXCEPTION",
  NO_HANDLER = "NO_HANDLER",
  TRANSPORT_ERROR = "TRANSPORT_ERROR",
  CONNECTION_TIMEOUT = "CONNECTION_TIMEOUT",
  WATCHDOG_TIMEOUT = "WATCHDOG_TIMEOUT",
  CIRCUIT_OPEN = "CIRCUIT_OPEN"
}

/**
 * Custom error class for the WebSocket subsystem.
 */
export class WebSocketError extends Error {
  public readonly code: WebSocketErrorCode;

  constructor(code: WebSocketErrorCode, message: string) {
    super(message);
    this.name = "WebSocketError";
    this.code = code;
  }
}
